import React, { useMemo } from 'react';
import styled from 'styled-components';
import { COLOR_SYSTEM } from 'components/design/design-system';

export interface ProgressGaugeProps {
  value: number;
  thresholds: number[]; // e.g., [0, 25, 33, 60]
  colors: string[]; // e.g., ['#10B981', '#F59E0B', '#EF4444']
  mode?: 'even' | 'linear'; // 'even': equal width, 'linear': proportional to value
  showIndicator?: boolean;
  width?: string;
  height?: string;
  indicatorColor?: string;
  className?: string;
  gradientWidth?: number; // percentage of gradient transition area (default: 10)
}

const GaugeContainer = styled.div<{ width?: string; height?: string }>`
  position: relative;
  width: ${props => props.width || '200px'};
  height: ${props => props.height || '20px'};
`;

const GaugeTrack = styled.div`
  width: 100%;
  height: 100%;
  background-color: #E5E7EB;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
`;

const GaugeSegment = styled.div<{ left: number; width: number; background: string }>`
  position: absolute;
  top: 0;
  left: ${props => props.left}%;
  width: ${props => props.width}%;
  height: 100%;
  background: ${props => props.background};
`;

const GaugeIndicator = styled.div<{ position: number; color?: string }>`
  position: absolute;
  top: 50%;
  left: ${props => props.position}%;
  transform: translate(-50%, -50%);
  width: 7px;
  height: 14px;
  background-color: ${COLOR_SYSTEM.get("Gray")[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[800]};
  border-radius: 30%;
  z-index: 10;
`;

const ProgressGauge: React.FC<ProgressGaugeProps> = ({
  value,
  thresholds,
  colors,
  mode = 'linear',
  showIndicator = true,
  width,
  height,
  indicatorColor,
  className,
  gradientWidth = 10
}) => {
  const segments = useMemo(() => {
    if (thresholds.length < 2 || colors.length !== thresholds.length - 1) {
      console.warn('ProgressGauge: Invalid thresholds or colors configuration');
      return [];
    }

    const min = thresholds[0];
    const max = thresholds[thresholds.length - 1];
    const numSegments = thresholds.length - 1;
    
    let accumulatedPosition = 0;
    const result = [];

    for (let i = 0; i < numSegments; i++) {
      const segmentMin = thresholds[i];
      const segmentMax = thresholds[i + 1];
      const color = colors[i];
      const nextColor = i < numSegments - 1 ? colors[i + 1] : null;
      
      let segmentWidth: number;
      
      if (mode === 'even') {
        // Each segment gets equal width
        segmentWidth = 100 / numSegments;
      } else {
        // Linear mode: width proportional to value range
        segmentWidth = ((segmentMax - segmentMin) / (max - min)) * 100;
      }

      // Main segment
      result.push({
        left: accumulatedPosition,
        width: segmentWidth * (1 - gradientWidth / 100),
        background: color
      });

      // Gradient transition (except for last segment)
      if (nextColor && i < numSegments - 1) {
        result.push({
          left: accumulatedPosition + segmentWidth * (1 - gradientWidth / 100),
          width: segmentWidth * (gradientWidth / 100),
          background: `linear-gradient(90deg, ${color}, ${nextColor})`
        });
      } else if (i === numSegments - 1) {
        // Last segment fills remaining space
        result.push({
          left: accumulatedPosition + segmentWidth * (1 - gradientWidth / 100),
          width: segmentWidth * (gradientWidth / 100),
          background: color
        });
      }

      accumulatedPosition += segmentWidth;
    }

    return result;
  }, [thresholds, colors, mode, gradientWidth]);

  const indicatorPosition = useMemo(() => {
    if (thresholds.length < 2) return 0;
    
    const min = thresholds[0];
    const max = thresholds[thresholds.length - 1];
    const clampedValue = Math.max(min, Math.min(value, max));
    
    if (mode === 'even') {
      // Find which segment the value falls into
      let segmentIndex = 0;
      for (let i = 0; i < thresholds.length - 1; i++) {
        if (clampedValue >= thresholds[i] && clampedValue <= thresholds[i + 1]) {
          segmentIndex = i;
          break;
        }
      }
      
      const numSegments = thresholds.length - 1;
      const segmentWidth = 100 / numSegments;
      const segmentMin = thresholds[segmentIndex];
      const segmentMax = thresholds[segmentIndex + 1];
      const positionInSegment = (clampedValue - segmentMin) / (segmentMax - segmentMin);
      
      return segmentIndex * segmentWidth + positionInSegment * segmentWidth;
    } else {
      // Linear mode
      return ((clampedValue - min) / (max - min)) * 100;
    }
  }, [value, thresholds, mode]);

  return (
    <GaugeContainer width={width} height={height} className={className}>
      <GaugeTrack>
        {segments.map((segment, index) => (
          <GaugeSegment
            key={index}
            left={segment.left}
            width={segment.width}
            background={segment.background}
          />
        ))}
      </GaugeTrack>
      {showIndicator && (
        <GaugeIndicator position={indicatorPosition} color={indicatorColor} />
      )}
    </GaugeContainer>
  );
};

export default ProgressGauge;